import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Grid,
  Box,
  IconButton,
  Typography,
  Slide,
  useTheme,
  styled,
  Stack,
  FormControlLabel,
  Checkbox,
  Link,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TextareaAutosize,
  Snackbar,
  Alert,
} from "@mui/material";
import callBackFormSubmit from "../services/callBackFormSu";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "white",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#0f9ffa",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0f9ffa",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#666",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#0f9ffa",
  },
}));

const StyledTextArea = styled(TextareaAutosize)(({ theme }) => ({
  width: "100%",
  borderRadius: "12px",
  padding: "16.5px 14px",
  fontSize: "1rem",
  fontFamily: "inherit",
  border: "1px solid #e0e0e0",
  "&:hover": {
    borderColor: "#00cc87",
  },
  "&:focus": {
    borderColor: "#00cc87",
    outline: "none",
  },
  "@media (max-width: 600px)": {
    fontSize: "16px", // Prevents zoom on iOS
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "12px",
  padding: "5px 30px",
  fontSize: "16px",
  marginTop: "20px",
  fontWeight: "bold",
  textTransform: "none",
  background: "linear-gradient(45deg, #00cc87 30%, #f44336 90%)",
  boxShadow: "0 8px 20px rgba(211, 47, 47, 0.3)",
  "&:hover": {
    background: "linear-gradient(45deg, #b71c1c 30%, #00cc87 90%)",
    boxShadow: "0 12px 25px rgba(211, 47, 47, 0.4)",
    transform: "translateY(-2px)",
  },
  transition: "all 0.3s ease",
  "@media (max-width: 600px)": {
    width: "100%",
    padding: "12px 30px",
  },
}));

// Create a responsive stack component
const ResponsiveStack = styled(Stack)({
  "@media (max-width: 600px)": {
    flexDirection: "column !important",
    alignItems: "flex-end !important",
    "& > *": {
      width: "100% !important",
      marginBottom: "10px !important",
    },
  },
});

// Create a responsive container for checkboxes
const ResponsiveCheckboxContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "wrap",
  "@media (max-width: 600px)": {
    flexDirection: "column",
    alignItems: "flex-start",
    "& > *": {
      marginBottom: "8px !important",
    },
  },
});

export default function FormOne() {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    "email-address": "",
    "phone-number": "",
    dob: "",
    "residential-address": "",
    "residential-address-code": "",
    "loan-product-interested-in": "",
    description: "",
    "estimated-loan-amount": "",
    declaration: false,
    terms: false,
  });

  const [errors, setErrors] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  // Check if screen is mobile size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    // Initial check
    checkMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Show popup after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setOpen(true);
    }, 30000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const validateField = (name, value) => {
    let errorMsg = "";

    switch (name) {
      case "fullName":
        if (!value.trim()) errorMsg = "Full name is required";
        else if (value.trim().split(" ").length < 2)
          errorMsg = "Please enter your full name";
        break;

      case "email":
        if (!value) errorMsg = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          errorMsg = "Invalid email format";
        break;

      case "phoneNumber":
        if (!value) errorMsg = "Phone number is required";
        else if (!/^[0-9]{10}$/.test(value.replace(/\D/g, "")))
          errorMsg = "Invalid phone number (10 digits)";
        break;

      case "loanProduct":
        if (!value) errorMsg = "Please select a loan product";
        break;

      case "loanAmount":
        if (!value) errorMsg = "Loan amount is required";
        else if (isNaN(value) || parseFloat(value) <= 0)
          errorMsg = "Must be a positive number";
        break;

      case "declaration":
        if (!value) errorMsg = "You must confirm the declaration";
        break;

      case "terms":
        if (!value) errorMsg = "You must agree to the terms";
        break;

      default:
        break;
    }

    return errorMsg;
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));

    // Validate field on change
    if (errors[name]) {
      const errorMsg = validateField(name, fieldValue);
      setErrors((prev) => ({
        ...prev,
        [name]: errorMsg,
      }));
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    const errorMsg = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: errorMsg,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((field) => {
      const errorMsg = validateField(field, formData[field]);
      if (errorMsg) {
        newErrors[field] = errorMsg;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      // First API call (your existing one)
      await callBackFormSubmit(formData);

      // Second API call for email notification
      const emailFormData = new FormData();

      // Set email credentials and details
      emailFormData.append("smtpEmail", "psnfinanceblr@gmail.com");
      emailFormData.append("appPassword", "pnyl ysdr vfxk avmj");
      emailFormData.append("toEmail", "psnfinanceblr@gmail.com");
      emailFormData.append(
        "subject",
        `New Loan Application - ${formData["loan-product-interested-in"]}`
      );

      // Create message content using your form fields
      const message = `New loan application received.\n\nName: ${formData.name}\nEmail: ${formData["email-address"]}\nPhone: ${formData["phone-number"]}\nDate of Birth: ${formData.dob}\nResidential Address: ${formData["residential-address"]}\nPostal Code: ${formData["residential-address-code"]}\nLoan Product: ${formData["loan-product-interested-in"]}\nDescription: ${formData.description}\nEstimated Loan Amount: ${formData["estimated-loan-amount"]}`;

      const html = `
      <h3>New Loan Application</h3>
      <p><strong>Name:</strong> ${formData.name}</p>
      <p><strong>Email:</strong> ${formData["email-address"]}</p>
      <p><strong>Phone:</strong> ${formData["phone-number"]}</p>
      <p><strong>Date of Birth:</strong> ${formData.dob}</p>
      <p><strong>Residential Address:</strong> ${formData["residential-address"]}</p>
      <p><strong>Postal Code:</strong> ${formData["residential-address-code"]}</p>
      <p><strong>Loan Product Interested In:</strong> ${formData["loan-product-interested-in"]}</p>
      <p><strong>Description:</strong> ${formData.description}</p>
      <p><strong>Estimated Loan Amount:</strong> ${formData["estimated-loan-amount"]}</p>
    `;

      emailFormData.append("message", message);
      emailFormData.append("html", html);

      // Send email notification
      const emailResponse = await fetch(
        "https://email-sender.backendprod.com/api/send-email",
        {
          method: "POST",
          headers: {
            "X-API-Key": "Dinesh@@@@",
          },
          body: emailFormData,
        }
      );

      const emailResult = await emailResponse.json();

      if (!emailResult.success) {
        console.error("Email sending failed:", emailResult.error);
        // We're not throwing an error here so the form submission is still considered successful
      }

      // Show success message
      setSnackbar({
        open: true,
        message: "Application submitted successfully!",
        severity: "success",
      });

      // Reset form after successful submission
      setFormData({
        name: "",
        "email-address": "",
        "phone-number": "",
        dob: "",
        "residential-address": "",
        "residential-address-code": "",
        "loan-product-interested-in": "",
        description: "",
        "estimated-loan-amount": "",
        declaration: false,
        terms: false,
      });
    } catch (error) {
      console.error("Submission error:", error);
      setSnackbar({
        open: true,
        message: "Failed to submit application. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const loanProducts = [
    "Select Loan Type",
    "Used Commercial Vehicle Loan",
    "Used Car Loan",
    "Used Truck Loan",
    "EV Car Loan",
    "EV Truck Loan",
    "EV 3 Wheeler Loan",
    "Business Loan",
  ];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
      <Stack spacing={2} sx={{ mt: 2 }}>
        {/* Full Name and Email - using responsive stack */}
        <ResponsiveStack spacing={2} direction="row" alignItems="center">
          <StyledTextField
            required
            fullWidth
            id="fullName"
            label="Full Name"
            name="name"
            value={formData.fullName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.fullName}
            helperText={errors.fullName}
            variant="outlined"
          />

          {/* Email Address */}
          <StyledTextField
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email-address"
            type="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.email}
            helperText={errors.email}
            variant="outlined"
          />
        </ResponsiveStack>

        {/* Phone Number and Date of Birth */}
        <ResponsiveStack spacing={2} direction="row" alignItems="center">
          <StyledTextField
            required
            fullWidth
            id="phoneNumber"
            label="Phone Number"
            name="phone-number"
            value={formData.phoneNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber}
            variant="outlined"
            inputProps={{
              pattern: "[0-9]*",
              inputMode: "numeric",
            }}
          />

          {/* Date of Birth */}
          <StyledTextField
            required
            fullWidth
            id="dob"
            label="Date of Birth"
            name="dob"
            type="date"
            value={formData.dob}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.dob}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
          />
        </ResponsiveStack>

        {/* Residential Address */}
        <ResponsiveStack spacing={2} direction="row" alignItems="center">
          <StyledTextField
            required
            fullWidth
            id="address"
            label="Residential Address"
            name="residential-address"
            value={formData.address}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.address}
            helperText={errors.address}
            variant="outlined"
          />

          {/* Zip/Postal Code */}
          <StyledTextField
            required
            fullWidth
            id="zipCode"
            label="Residential Address (Zip/Postal Code)"
            name="residential-address-code"
            value={formData.zipCode}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.zipCode}
            helperText={errors.zipCode}
            variant="outlined"
          />
        </ResponsiveStack>

        {/* Loan Product and Loan Amount */}
        <ResponsiveStack spacing={2} direction="row" alignItems="center">
          {/* Loan Product */}
          <FormControl
            fullWidth
            variant="outlined"
            error={!!errors.loanProduct}
          >
            <InputLabel id="loan-product-label">
              Loan Product Interested In
            </InputLabel>
            <Select
              labelId="loan-product-label"
              id="loanProduct"
              name="loan-product-interested-in"
              value={formData["loan-product-interested-in"]}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Loan Product Interested In"
            >
              <MenuItem value="">
                <em>Select a loan product</em>
              </MenuItem>
              {loanProducts.map((product, index) => (
                <MenuItem key={index} value={product}>
                  {product}
                </MenuItem>
              ))}
            </Select>
            {errors.loanProduct && (
              <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                {errors.loanProduct}
              </Typography>
            )}
          </FormControl>

          <StyledTextField
            required
            fullWidth
            id="loanAmount"
            label="Estimated Loan Amount"
            name="estimated-loan-amount"
            value={formData.loanAmount}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.loanAmount}
            helperText={errors.loanAmount}
            variant="outlined"
            inputProps={{
              min: "1",
              step: "1000",
            }}
          />
        </ResponsiveStack>

        {/* Description */}
        <Box>
          <StyledTextArea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            minRows={3}
            placeholder="Describe your loan requirements..."
          />
        </Box>

        {/* Declaration and Terms - using responsive container */}
        <ResponsiveCheckboxContainer sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                name="declaration"
                checked={formData.declaration}
                onChange={handleChange}
                color="primary"
              />
            }
            label="I declare that the information provided is true and accurate"
          />
          {errors.declaration && (
            <Typography variant="caption" color="error">
              {errors.declaration}
            </Typography>
          )}

          {/* Terms & Conditions */}
          <FormControlLabel
            sx={{ '& .MuiFormControlLabel-asterisk': { display: 'none' } }}
            control={
              <Checkbox
                name="terms"
                checked={formData.terms}
                onChange={handleChange}
                color="primary"
              />
            }
            label={
              <Typography component="span" variant="body1" sx={{ display: 'inline', fontSize: { xs: "12px", md: "14px" }, textAlign: "left" }}>
                By continuing, you agree to provide your authorization to PSN Finance, PSN Group, and its affiliated entities to store and process your data as per the{" "}
                <Link component={RouterLink} to="/privacy-policy" underline="hover" color="primary">
                  Privacy Policy*
                </Link>
                .
              </Typography>
            }
          />
          {errors.terms && (
            <Typography variant="caption" color="error">
              {errors.terms}
            </Typography>
          )}
        </ResponsiveCheckboxContainer>
      </Stack>

      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 2,
            width: { xs: "50%" },
            marginX: "auto",
          }}
        >
          <StyledButton
            type="submit"
            variant="contained"
            id="submit-application-form"
            size="small"
            fullWidth={isMobile}
            sx={{
              fontSize: isMobile ? "12px" : "14px",
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit Application"}
          </StyledButton>
        </Box>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
